import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Car, PlusCircle, UserRound, Wrench } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { PlateChip } from '../components/PlateChip';
import { StatusPill, STATUS_META } from '../components/StatusPill';
import { EmptyState } from '../components/EmptyState';
import { api, errMsg } from '../lib/api';
import { peso } from '../lib/format';
import { useAuth } from '../lib/AuthContext';

const COLUMNS = ['received', 'estimate_sent', 'approved', 'in_progress', 'ready', 'closed'];

function JobCard({ job, onClick }) {
  return (
    <button
      data-testid="workboard-job-card"
      onClick={onClick}
      className={`w-full text-left rounded-2xl border border-black/5 bg-white p-3 shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-card)] transition-shadow active:scale-[0.995] ${
        job.status === 'ready' ? 'ring-2 ring-[hsl(var(--status-ready-fg))]/25' : ''
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <PlateChip plate={job.plate} />
        <span className="font-plate text-xs text-[hsl(var(--muted-foreground))]">{job.jo_number}</span>
      </div>
      <div className="mt-2 text-sm font-medium">{job.vehicle}</div>
      <div className="mt-0.5 text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1">
        <UserRound className="h-3 w-3" /> {job.customer_name}
      </div>
      <div className="mt-2 flex items-center justify-between">
        <span className="text-sm font-semibold tabular-nums">{peso(job.total_centavos)}</span>
        {job.assigned_technician && (
          <span className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1">
            <Wrench className="h-3 w-3" /> {job.assigned_technician}
          </span>
        )}
      </div>
    </button>
  );
}

export default function WorkboardPage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const [jobs, setJobs] = useState(null);
  const [q, setQ] = useState('');
  const [showVoided, setShowVoided] = useState(false);
  const [tab, setTab] = useState('received');

  const load = async (voided = showVoided) => {
    try {
      const { data } = await api.get('/jobs', { params: voided ? { include_voided: 1 } : {} });
      setJobs(data.jobs);
    } catch (e) {
      toast.error(errMsg(e));
      setJobs([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showVoided]);

  const filtered = useMemo(() => {
    if (!jobs) return [];
    const ql = q.trim().toLowerCase();
    if (!ql) return jobs;
    return jobs.filter((j) =>
      `${j.plate} ${j.customer_name} ${j.customer_mobile} ${j.jo_number}`.toLowerCase().includes(ql)
    );
  }, [jobs, q]);

  const byStatus = useMemo(() => {
    const map = {};
    COLUMNS.concat(['voided']).forEach((s) => { map[s] = []; });
    filtered.forEach((j) => { (map[j.status] = map[j.status] || []).push(j); });
    return map;
  }, [filtered]);

  const cols = showVoided ? COLUMNS.concat(['voided']) : COLUMNS;

  if (jobs === null) {
    return <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>;
  }

  const isEmpty = jobs.length === 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold font-display">Work board</h1>
        <Button data-testid="workboard-new-intake-button" onClick={() => navigate('/intake')} className="h-10 rounded-xl">
          <PlusCircle className="h-4 w-4 mr-1.5" /> New intake
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          <Input
            data-testid="workboard-search-input"
            placeholder="Search plate, customer, mobile, or JO number"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-11 rounded-xl pl-9"
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
          <Switch data-testid="workboard-show-voided-switch" checked={showVoided} onCheckedChange={setShowVoided} />
          Show voided
        </label>
      </div>

      {isEmpty ? (
        <EmptyState
          icon={Car}
          title="No vehicles in the shop."
          hint="Receive your first vehicle to get started."
          actionLabel="Receive your first vehicle"
          onAction={() => navigate('/intake')}
          testId="workboard-empty-state"
        />
      ) : (
        <>
          {/* Mobile: tabs per status */}
          <div className="md:hidden space-y-3">
            <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-4 px-4">
              {cols.map((s) => (
                <button
                  key={s}
                  data-testid={`workboard-tab-${s}`}
                  onClick={() => setTab(s)}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
                    tab === s ? 'bg-[hsl(var(--foreground))] text-white border-transparent' : 'bg-white border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))]'
                  }`}
                >
                  {STATUS_META[s].label} ({(byStatus[s] || []).length})
                </button>
              ))}
            </div>
            <div className="space-y-2.5">
              {(byStatus[tab] || []).length === 0 ? (
                <EmptyState title={`No jobs in '${STATUS_META[tab].label}'.`} hint="Create a new intake or check another column." testId="workboard-column-empty" />
              ) : (
                (byStatus[tab] || []).map((j) => <JobCard key={j.id} job={j} onClick={() => navigate(`/jobs/${j.id}`)} />)
              )}
            </div>
          </div>

          {/* Desktop: kanban columns */}
          <div className="hidden md:flex gap-4 overflow-x-auto pb-4">
            {cols.map((s) => (
              <div key={s} className="w-[300px] shrink-0 space-y-2.5">
                <div className="flex items-center justify-between py-1">
                  <StatusPill status={s} />
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">{(byStatus[s] || []).length}</span>
                </div>
                {(byStatus[s] || []).map((j) => <JobCard key={j.id} job={j} onClick={() => navigate(`/jobs/${j.id}`)} />)}
                {(byStatus[s] || []).length === 0 && (
                  <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-4 text-center text-xs text-[hsl(var(--muted-foreground))]">
                    No jobs
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
